import { Component, type ReactNode } from 'react';
import { Paper, Title, Text, Button, Stack, Code, Group } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  /** Optional name shown in the error UI (e.g. "Dashboard", "Monitoring") */
  label?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to backend for monitoring
    const payload = {
      message: error.message,
      stack: error.stack?.slice(0, 2000),
      componentStack: info.componentStack?.slice(0, 2000),
      url: window.location.pathname,
      timestamp: new Date().toISOString(),
    };

    fetch('/monitoring/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url: window.location.pathname,
        error: payload.message,
        // Store as a CWV-style report so it shows in monitoring
        lcp: 0, inp: 0, cls: 0,
      }),
    }).catch(() => { /* best-effort logging */ });

    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const label = this.props.label || 'This section';

    return (
      <Paper withBorder p="xl" radius="md" m="md">
        <Stack gap="md" align="center">
          <IconAlertTriangle size={48} color="var(--mantine-color-orange-6)" />
          <Title order={3}>{label} encountered an error</Title>
          <Text c="dimmed" ta="center" maw={500}>
            Something went wrong rendering this part of the page.
            You can try again or reload the page. If the problem persists,
            use the bug report button to flag it.
          </Text>
          {this.state.error && (
            <Code block style={{ maxWidth: 500, overflow: 'auto', maxHeight: 120 }}>
              {this.state.error.message}
            </Code>
          )}
          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={this.handleRetry}
              variant="light"
            >
              Try Again
            </Button>
            <Button
              onClick={this.handleReload}
              variant="subtle"
              color="gray"
            >
              Reload Page
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }
}
